/**
 * 雲端同步 —— 本機 profile/progress ⇄ Supabase `cloud_progress` 表。
 *
 * 「不同人的學習進度完全切開」由資料庫 RLS 強制(每列綁 user_id = 登入者,
 * 別人連查都查不到)。這層只負責「本機 ⇄ 雲端」的搬運:
 *
 *   - pushProfileToCloud(id)  : 把單一 profile 的 progress + nickname 上傳(upsert)。
 *   - pullCloudIntoLocal()    : 把登入者所有列拉回,**直接寫 localStorage**
 *                               (重建 FamilyState.profiles + 每個 progress key),
 *                               **不走 saveProgress**(否則觸發 mirror 回送迴圈)。
 *   - pushAllLocalToCloud()   : 把本機所有 profile 上傳(首次登入遷移用)。
 *
 * 鐵則:`isCloudEnabled()` 為 false 或未登入 → 全部 no-op;任何錯誤吞掉不 throw
 * (降級成純本機,絕不阻斷既有流程)。寫 localStorage 時用 withMirrorSuppressed
 * 包住,確保即使未來改走 saveProgress 也不會回送。
 */
import type { PlayerProgress, ProfileMeta, FamilyState, AvatarColor } from './types';
import { AVATAR_COLORS } from './types';
import { getSupabase, isCloudEnabled, getCurrentUser } from './supabase';
import { loadFamilyState, saveFamilyState, listProfiles } from './profile';
import { withMirrorSuppressed } from './progress';

const TABLE = 'cloud_progress';
const FAMILY_KEY = 'eternal-fortress.family';
const profileStorageKey = (profileId: string) => `eternal-fortress.progress.${profileId}`;

/** 「合併 bug」留下的垃圾 profile 暱稱判斷(含「(本機)」)。 */
const isJunkNickname = (nickname: string | null | undefined): boolean =>
  typeof nickname === 'string' && nickname.includes('(本機)');

/** 雲端一列(對應 supabase/schema.sql 的 cloud_progress)。 */
interface CloudRow {
  profile_id: string;
  nickname: string | null;
  data: PlayerProgress;
  updated_at?: string;
}

/** 直接讀 localStorage 的某 profile progress(不經 migrate,只為上傳整包)。 */
function readLocalProgressRaw(profileId: string): PlayerProgress | null {
  const raw = localStorage.getItem(profileStorageKey(profileId));
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as PlayerProgress;
  } catch {
    return null;
  }
}

/** 把 ISO 時戳轉成 ms(壞值/缺值 → 0)。純函式。 */
export function lastSavedMs(p: { lastSavedAt?: string } | null | undefined): number {
  if (!p || typeof p.lastSavedAt !== 'string') return 0;
  const t = Date.parse(p.lastSavedAt);
  return Number.isFinite(t) ? t : 0;
}

/**
 * pull 落地時:是否「保留本地、不被雲端覆寫」。
 * boss 2026-06-13「遊戲紀錄沒有儲存」根因守門:本地 lastSavedAt **嚴格較新** →
 * 保留本地(玩完還沒 push 的較新進度不被雲端舊版蓋掉);雲端較新或一樣新或本地
 * 缺/壞 → 用雲端(正常同步,平手讓雲端贏以收斂)。純函式,讀一次 localStorage。
 */
export function shouldKeepLocalOverCloud(
  profileId: string,
  cloudData: { lastSavedAt?: string },
): boolean {
  const local = readLocalProgressRaw(profileId);
  if (!local) return false; // 本地沒有 → 用雲端
  return lastSavedMs(local) > lastSavedMs(cloudData);
}

/** 該 profile 的暱稱(查 FamilyState;查不到回 null,讓雲端欄位留空)。 */
function nicknameFor(profileId: string): string | null {
  const meta = listProfiles().find((p) => p.id === profileId);
  return meta?.nickname ?? null;
}

/**
 * 把單一 profile 的 progress + nickname 上傳。user_id 由 auth/RLS 自動帶,
 * 衝突鍵 (user_id, profile_id) → upsert。雲端未設定/未登入/本機無此 progress
 * → no-op。錯誤吞掉。
 */
export async function pushProfileToCloud(profileId: string): Promise<void> {
  try {
    if (!isCloudEnabled()) return;
    const sb = getSupabase();
    if (!sb) return;
    const user = await getCurrentUser();
    if (!user) return;
    const nickname = nicknameFor(profileId);
    // 絕不把「(本機)」垃圾 profile 上傳(否則它在雲端永生、跨裝置又洪水)。
    if (isJunkNickname(nickname)) return;
    const data = readLocalProgressRaw(profileId);
    if (!data) return;
    await sb.from(TABLE).upsert(
      {
        user_id: user.id,
        profile_id: profileId,
        nickname,
        data,
      },
      { onConflict: 'user_id,profile_id' },
    );
  } catch {
    // 降級成純本機 —— 同步失敗絕不阻斷遊戲。
  }
}

/**
 * 永久刪除雲端登入者所有「(本機)」垃圾列(merge bug 增生的)。登入合併時呼叫,
 * 讓洪水不會在下次 pull 或換裝置時復活。錯誤吞掉。
 */
export async function deleteJunkFromCloud(): Promise<void> {
  try {
    if (!isCloudEnabled()) return;
    const sb = getSupabase();
    if (!sb) return;
    const user = await getCurrentUser();
    if (!user) return;
    await sb.from(TABLE).delete().eq('user_id', user.id).like('nickname', '%(本機)%');
  } catch {
    /* 清雲端失敗不阻斷 */
  }
}

/**
 * 把本機所有 profile 上傳(首次登入、雲端尚無列時的遷移)。逐筆 push,
 * 任何單筆失敗不影響其他筆(pushProfileToCloud 自己吞錯)。
 */
export async function pushAllLocalToCloud(): Promise<void> {
  try {
    if (!isCloudEnabled()) return;
    for (const meta of listProfiles()) {
      await pushProfileToCloud(meta.id);
    }
  } catch {
    /* no-op on failure */
  }
}

/**
 * 拉登入者所有列 → **直接寫 localStorage**:
 *   1. 每列 data 寫進 `eternal-fortress.progress.<profile_id>`。
 *   2. 重建/合併 FamilyState.profiles(雲端的 profile_id + nickname);
 *      activeProfileId 保留本機現值(雲端缺則挑第一筆)。
 *
 * **不經 saveProgress**(避免 mirror 回送);仍以 withMirrorSuppressed 包住做
 * 雙保險。回傳拉了幾筆(0 = 雲端無資料 / 未啟用 / 未登入 / 失敗)。
 */
export async function pullCloudIntoLocal(): Promise<number> {
  try {
    if (!isCloudEnabled()) return 0;
    const sb = getSupabase();
    if (!sb) return 0;
    const user = await getCurrentUser();
    if (!user) return 0;

    const { data, error } = await sb.from(TABLE).select('*');
    if (error || !Array.isArray(data) || data.length === 0) return 0;

    // 過濾掉雲端殘留的「(本機)」垃圾列 —— 否則 pull 會把洪水重建回選單(boss 2026-06-09
    // 回報「怎麼沒修」:清在前、拉在後又洪水)。垃圾列另由 deleteJunkFromCloud 永久清除。
    const rows = (data as CloudRow[]).filter((r) => !isJunkNickname(r?.nickname));
    if (rows.length === 0) return 0;

    withMirrorSuppressed(() => {
      // 1) 每列 progress 落地 —— 但**比 lastSavedAt,本地較新就保留,絕不被雲端較舊覆寫**。
      //    (boss 2026-06-13「遊戲紀錄沒有儲存」根因:原本無條件 setItem 覆寫,玩完
      //    還沒 push / push 失敗時,下次登入 pull 把本地較新進度蓋成雲端舊版 → 蒸發。)
      //    雲端較新或本地缺/壞 → 用雲端(正常跨裝置同步)。
      for (const row of rows) {
        if (!row || typeof row.profile_id !== 'string' || !row.data) continue;
        if (shouldKeepLocalOverCloud(row.profile_id, row.data)) continue;
        localStorage.setItem(profileStorageKey(row.profile_id), JSON.stringify(row.data));
      }

      // 2) 重建 FamilyState.profiles —— 以雲端列為準,沿用本機既有 meta
      //    (createdAt / avatarColor),雲端 nickname 覆寫。
      const existing = loadFamilyState();
      const existingById = new Map<string, ProfileMeta>(
        (existing?.profiles ?? []).map((p) => [p.id, p]),
      );

      const profiles: ProfileMeta[] = rows
        .filter((r) => r && typeof r.profile_id === 'string')
        .map((r, i) => {
          const prev = existingById.get(r.profile_id);
          const avatarColor: AvatarColor =
            prev?.avatarColor ?? AVATAR_COLORS[i % AVATAR_COLORS.length];
          return {
            id: r.profile_id,
            nickname: r.nickname ?? prev?.nickname ?? '指揮官',
            createdAt: prev?.createdAt ?? Date.now(),
            avatarColor,
          };
        });

      if (profiles.length === 0) return;

      // activeProfileId:保留本機現值(若仍在雲端清單內),否則挑第一筆。
      const prevActive = existing?.activeProfileId;
      const activeProfileId =
        prevActive && profiles.some((p) => p.id === prevActive)
          ? prevActive
          : profiles[0].id;

      const nextState: FamilyState = {
        ...(existing?.familyCode ? { familyCode: existing.familyCode } : {}),
        profiles,
        activeProfileId,
      };
      // saveFamilyState 不走 progress mirror;直接寫(此處用 localStorage 一致性)。
      saveFamilyState(nextState);
      // 防禦:saveFamilyState 內部就是 localStorage.setItem(FAMILY_KEY, …),
      // 顯式引用 FAMILY_KEY 讓 key 不漂移(若上游改 key,這裡編譯期就會對不上)。
      void FAMILY_KEY;
    });

    return rows.length;
  } catch {
    return 0;
  }
}

/** 把某 profile 的 progress 寫回 localStorage(不觸發 mirror)。 */
function writeLocalProgressRaw(profileId: string, data: PlayerProgress): void {
  withMirrorSuppressed(() => {
    localStorage.setItem(profileStorageKey(profileId), JSON.stringify(data));
  });
}

/** 確保 FamilyState 內有此 profile(pull 重建時可能漏掉本機獨有的)。 */
function ensureProfileInFamily(id: string, nickname: string | null): void {
  const st = loadFamilyState();
  const profiles = st?.profiles ?? [];
  if (profiles.some((p) => p.id === id)) return;
  const meta: ProfileMeta = {
    id,
    nickname: nickname ?? '指揮官',
    createdAt: Date.now(),
    avatarColor: AVATAR_COLORS[profiles.length % AVATAR_COLORS.length] as AvatarColor,
  };
  withMirrorSuppressed(() => {
    saveFamilyState({
      ...(st?.familyCode ? { familyCode: st.familyCode } : {}),
      profiles: [...profiles, meta],
      activeProfileId: st?.activeProfileId ?? id,
    });
  });
}

/**
 * 一次性清掉「合併 bug」在本機 FamilyState 留下的「(本機)」垃圾 profile
 * (含它們的 progress key)。開機(initAccountSync)呼一次;沒有垃圾就 no-op。
 * 全部至少留一個 profile;清完不可空。寫入用 withMirrorSuppressed 避免回送雲端。
 */
export function purgeJunkProfiles(): void {
  try {
    const st = loadFamilyState();
    if (!st || !Array.isArray(st.profiles)) return;
    const isJunk = (nickname: string): boolean => nickname.includes('(本機)');
    const junk = st.profiles.filter((p) => isJunk(p.nickname));
    if (junk.length === 0) return;

    for (const j of junk) {
      try { localStorage.removeItem(profileStorageKey(j.id)); } catch { /* noop */ }
    }
    let profiles = st.profiles.filter((p) => !isJunk(p.nickname));
    if (profiles.length === 0) {
      profiles = [{ id: 'default', nickname: '指揮官', createdAt: Date.now(), avatarColor: AVATAR_COLORS[0] as AvatarColor }];
    }
    const activeProfileId = profiles.some((p) => p.id === st.activeProfileId)
      ? st.activeProfileId
      : profiles[0].id;
    withMirrorSuppressed(() => {
      saveFamilyState({
        ...(st.familyCode ? { familyCode: st.familyCode } : {}),
        profiles,
        activeProfileId,
      });
    });
  } catch {
    /* noop */
  }
}

/**
 * 一個 progress 是否「從沒真的用過」(全新 baseline)。用來**安全地**併掉重複空帳:
 * 只要有任何學習/遊戲痕跡(XP / 錯題 / KP / 每日XP / streak / 技能)就不算空,一律保留。
 */
function isEmptyProgress(p: PlayerProgress | null): boolean {
  if (!p) return true;
  const noXP = !(p.totalXP || p.freeXP || p.commanderXp);
  const noFaction = !p.factionXP || Object.values(p.factionXP).every((v) => !v);
  const noLearning = !(p.mistakes?.length) && !(p.studentKPs?.length);
  const noDaily = !p.dailyXP || Object.keys(p.dailyXP).length === 0;
  const noStreak = !p.streak || !(p.streak.days);
  const noSkills = !p.skillLevels || Object.keys(p.skillLevels).length === 0;
  return noXP && noFaction && noLearning && noDaily && noStreak && noSkills;
}

/**
 * 進度的「實質指紋」—— 判斷兩個 profile 是不是同一份(複製品)。只看實質欄位
 * (XP/錯題/KP/每日XP/streak/技能/門派),忽略 lastSavedAt 這種時間戳,所以
 * 「同一份在不同時間各存一次」也算相同。
 */
function progressFingerprint(p: PlayerProgress | null): string {
  if (!p) return 'none';
  return JSON.stringify({
    t: p.totalXP ?? 0,
    f: p.freeXP ?? 0,
    c: p.commanderXp ?? 0,
    fx: p.factionXP ?? {},
    m: p.mistakes?.length ?? 0,
    k: p.studentKPs?.length ?? 0,
    d: p.dailyXP ?? {},
    s: p.streak?.days ?? 0,
    sk: p.skillLevels ?? {},
  });
}

/**
 * 併掉「同暱稱的重複帳」—— merge bug 在本機/雲端留下一堆同名「指揮官」(無「(本機)」
 * 字樣,purgeJunkProfiles 擋不到;boss 2026-06-09「一樣喔」實測:10 筆其實是同一份
 * 存檔複製,distinct fingerprint = 1,且非空[有 free/cmd] → 舊的「只刪空帳」收不掉)。
 *
 * 規則(絕不誤刪真進度):每個暱稱分組 ——
 *   · 有「非空」帳 → 依**實質指紋**去重(同一份複製品只留一個,優先 active/資料多),
 *     並丟掉所有空帳;內容真的不同的非空帳全部保留。
 *   · 全空 → 留一個(優先 active,否則最早建立),其餘丟。
 * 回傳被丟掉的 profileId 陣列(供雲端一併刪除)。寫入用 withMirrorSuppressed。
 */
export function dedupeDuplicateProfiles(): string[] {
  try {
    const st = loadFamilyState();
    if (!st || !Array.isArray(st.profiles) || st.profiles.length <= 1) return [];

    const byNick = new Map<string, ProfileMeta[]>();
    for (const p of st.profiles) {
      const g = byNick.get(p.nickname) ?? [];
      g.push(p);
      byNick.set(p.nickname, g);
    }

    const dataLen = (id: string): number => localStorage.getItem(profileStorageKey(id))?.length ?? 0;
    // 偏好留下的順序:**資料量最大優先**(絕不丟掉最完整那份)→ 同樣大則 active 優先 →
    // 再以建立早為準。richness 擺第一是安全鐵則:即使 active 是空帳,也會留住有進度那份。
    const preferFirst = (a: ProfileMeta, b: ProfileMeta): number =>
      dataLen(b.id) - dataLen(a.id) ||
      (a.id === st.activeProfileId ? -1 : 0) - (b.id === st.activeProfileId ? -1 : 0) ||
      a.createdAt - b.createdAt;

    // 系統自動建立的預設名;出現多個一定是「同一個預設帳被複製」→ 一律併成最豐富的一個。
    const DEFAULT_NICKNAME = '指揮官';

    const keep: ProfileMeta[] = [];
    const dropIds: string[] = [];
    for (const [nickname, group] of byNick) {
      if (group.length === 1) {
        keep.push(group[0]);
        continue;
      }
      if (nickname === DEFAULT_NICKNAME) {
        // 預設名:不論內容小差異,併成資料最完整的一個(boss 2026-06-09「還是有兩個」:
        // 兩份只差登入連續天數之類的小欄位,指紋去重併不掉 → 預設名直接收斂成 1)。
        const sorted = [...group].sort(preferFirst);
        keep.push(sorted[0]);
        for (const p of sorted.slice(1)) dropIds.push(p.id);
        continue;
      }
      const nonEmpty = group.filter((p) => !isEmptyProgress(readLocalProgressRaw(p.id)));
      if (nonEmpty.length > 0) {
        // 非空:依實質指紋去重(同份複製品留一個);空帳全丟。
        const seen = new Set<string>();
        for (const p of [...nonEmpty].sort(preferFirst)) {
          const fp = progressFingerprint(readLocalProgressRaw(p.id));
          if (seen.has(fp)) dropIds.push(p.id);
          else { seen.add(fp); keep.push(p); }
        }
        for (const p of group) if (isEmptyProgress(readLocalProgressRaw(p.id))) dropIds.push(p.id);
      } else {
        // 全空:留一個。
        const sorted = [...group].sort(preferFirst);
        keep.push(sorted[0]);
        for (const p of sorted.slice(1)) dropIds.push(p.id);
      }
    }

    if (dropIds.length === 0) return [];
    for (const id of dropIds) {
      try { localStorage.removeItem(profileStorageKey(id)); } catch { /* noop */ }
    }
    const activeProfileId = keep.some((p) => p.id === st.activeProfileId)
      ? st.activeProfileId
      : keep[0].id;
    withMirrorSuppressed(() => {
      saveFamilyState({
        ...(st.familyCode ? { familyCode: st.familyCode } : {}),
        profiles: keep,
        activeProfileId,
      });
    });
    return dropIds;
  } catch {
    return [];
  }
}

/** 從雲端刪除指定 profile_id 的列(dedupe 掉的空帳)。錯誤吞掉。 */
export async function deleteProfilesFromCloud(profileIds: string[]): Promise<void> {
  try {
    if (profileIds.length === 0 || !isCloudEnabled()) return;
    const sb = getSupabase();
    if (!sb) return;
    const user = await getCurrentUser();
    if (!user) return;
    await sb.from(TABLE).delete().eq('user_id', user.id).in('profile_id', profileIds);
  } catch {
    /* 清雲端失敗不阻斷 */
  }
}

/**
 * 登入時的「合併」—— 本機與雲端都保留,不覆蓋、不丟資料(boss 2026-06-08)。
 *   - 雲端空 → 把本機全部搬上去(遷移)。
 *   - 雲端有料 → pull 下來(同 id 以雲端為準),再把「本機獨有(雲端沒有此 id)」的
 *     profile 以**同 id** upsert 回雲端(聯集,冪等)。同 id 衝突一律雲端為準,
 *     **絕不另存新 profile** —— 舊版「另存『{暱稱}(本機)』」會每次登入無限增生成幾百個,
 *     已於 2026-06-08 移除。
 *   - 收尾固定跑 self-heal:刪雲端「(本機)」垃圾列 + 併同名重複帳(預設名收斂成 1),
 *     雙保險根除增生(2026-06-09)。
 * 防增生鐵則:本流程只用既有 id upsert(不 createProfile)→ 冪等;重複登入/同步
 * N 次,profile 數不增。任何錯誤吞掉,不阻斷登入。
 */
export async function mergeCloudOnSignIn(): Promise<void> {
  try {
    if (!isCloudEnabled()) return;
    const sb = getSupabase();
    if (!sb) return;
    const user = await getCurrentUser();
    if (!user) return;

    // 1) snapshot 本機(有進度、非垃圾的 profile)—— 垃圾不納入聯集,避免又被加回。
    const localBefore = listProfiles()
      .filter((m) => !isJunkNickname(m.nickname))
      .map((m) => ({ id: m.id, nickname: m.nickname, data: readLocalProgressRaw(m.id) }))
      .filter((x): x is { id: string; nickname: string; data: PlayerProgress } => x.data !== null);

    // 2) 取雲端列。
    const { data, error } = await sb.from(TABLE).select('*');
    const cloudRows: CloudRow[] = !error && Array.isArray(data) ? (data as CloudRow[]) : [];

    // 雲端空 → 遷移本機上去。
    if (cloudRows.length === 0) {
      await pushAllLocalToCloud();
      return;
    }

    const cloudById = new Map(cloudRows.map((r) => [r.profile_id, r]));

    // 3) 雲端 → 本機(同 id 以雲端為準、重建名單)。
    await pullCloudIntoLocal();

    // 4) 只「聯集」本機獨有的 profile(雲端沒有此 id)→ 復原 + 加回名單 + 用「同 id」上傳。
    //    這是冪等的(同 id upsert),重複同步不會增生。
    //    同 id 衝突 → 雲端為準(pull 已套用),**絕不另存新 profile**
    //    —— 之前的「另存(本機)」會在每次同步無限增生成幾百個,已移除(2026-06-08 修)。
    for (const lp of localBefore) {
      if (cloudById.has(lp.id)) continue; // 同 id → 雲端為準,跳過
      writeLocalProgressRaw(lp.id, lp.data);
      ensureProfileInFamily(lp.id, lp.nickname);
      await pushProfileToCloud(lp.id);
    }

    // 5) 收尾:清「(本機)」垃圾 + 併「同名重複空帳」(boss 2026-06-09「怎麼沒修」→「一樣喔」)。
    //    pull 已過濾「(本機)」不重建;這裡永久刪雲端垃圾列 + 清本機名單;再把同名空帳
    //    併成一個,連雲端那幾列也刪掉,根除洪水來源(只併「完全沒用過」的,真進度全保留)。
    await deleteJunkFromCloud();
    purgeJunkProfiles();
    const droppedDupes = dedupeDuplicateProfiles();
    await deleteProfilesFromCloud(droppedDupes);
  } catch {
    /* 合併失敗不阻斷 */
  }
}
